import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import LoginPage from '../Members/LoginPage';

type JobStatus = 'pending' | 'processing' | 'printed' | 'error';

interface QueueChild {
  childFirstName: string;
  childLastName: string;
}

interface QueueEntry {
  id: string;
  children: QueueChild[];
  status: JobStatus;
  requestedBy?: string;
  date?: string;
  requestedAt?: { toDate: () => Date } | null;
  claimedAt?: { toDate: () => Date } | null;
  printedAt?: { toDate: () => Date } | null;
  error?: string;
}

const STATUS_FILTERS: (JobStatus | 'all')[] = [
  'all',
  'pending',
  'processing',
  'printed',
  'error',
];

const statusColor: Record<JobStatus, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  processing: 'warning',
  printed: 'success',
  error: 'error',
};

const STUCK_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes claimed with no result

const toDate = (t?: { toDate: () => Date } | null): Date | null =>
  t ? t.toDate() : null;

const relative = (t?: { toDate: () => Date } | null) => {
  const d = toDate(t);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : '—';
};

const isStuck = (entry: QueueEntry): boolean => {
  if (entry.status !== 'processing') return false;
  const claimed = toDate(entry.claimedAt);
  if (!claimed) return false;
  return Date.now() - claimed.getTime() > STUCK_THRESHOLD_MS;
};

const PrintQueueMonitor: React.FC = () => {
  const { userData, loading: authLoading, isChildcareAdmin } = useAuth();
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [filter, setFilter] = useState<JobStatus | 'all'>('all');
  const [actionError, setActionError] = useState('');
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (!userData || !isChildcareAdmin) return;
    const q = query(
      collection(db, 'printQueue'),
      orderBy('requestedAt', 'desc'),
      limit(200)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() } as QueueEntry)));
    });
    return () => unsubscribe();
  }, [userData, isChildcareAdmin]);

  // Re-render periodically so relative times and the "stuck" flag stay fresh.
  useEffect(() => {
    const interval = setInterval(() => forceTick((n) => n + 1), 15000);
    return () => clearInterval(interval);
  }, []);

  const counts = useMemo(() => {
    const c: Record<JobStatus, number> = {
      pending: 0,
      processing: 0,
      printed: 0,
      error: 0,
    };
    entries.forEach((e) => {
      if (c[e.status] !== undefined) c[e.status]++;
    });
    return c;
  }, [entries]);

  const stuckCount = useMemo(() => entries.filter(isStuck).length, [entries]);

  const visibleEntries = useMemo(
    () => (filter === 'all' ? entries : entries.filter((e) => e.status === filter)),
    [entries, filter]
  );

  const retry = async (id: string) => {
    setActionError('');
    try {
      await updateDoc(doc(db, 'printQueue', id), {
        status: 'pending',
        error: null,
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Retry failed');
    }
  };

  const resetStuck = async (id: string) => {
    setActionError('');
    try {
      await updateDoc(doc(db, 'printQueue', id), {
        status: 'pending',
        claimedAt: null,
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Reset failed');
    }
  };

  const removeJob = async (id: string) => {
    setActionError('');
    try {
      await deleteDoc(doc(db, 'printQueue', id));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  if (authLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Typography variant="h6" textAlign="center" gutterBottom>
          Admin Sign In
        </Typography>
        <LoginPage onUserSet={Function.prototype as () => void} />
      </Container>
    );
  }

  if (!isChildcareAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Typography variant="h6" color="error" textAlign="center">
          Access Denied
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 1 }}
        >
          You need the childcare role to view this page.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Print Queue Monitor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Live view of the <code>printQueue</code> collection (most recent 200
        jobs).
      </Typography>

      {stuckCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {stuckCount} job{stuckCount > 1 ? 's have' : ' has'} been
          &quot;processing&quot; for over 2 minutes without finishing — the
          print server may be offline or stuck. Use Reset to put a job back
          in the pending queue.
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {STATUS_FILTERS.map((s) => (
          <Chip
            key={s}
            label={
              s === 'all'
                ? `All (${entries.length})`
                : `${s[0].toUpperCase()}${s.slice(1)} (${counts[s]})`
            }
            color={s === 'all' ? 'primary' : statusColor[s]}
            variant={filter === s ? 'filled' : 'outlined'}
            onClick={() => setFilter(s)}
          />
        ))}
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Children</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Claimed</TableCell>
              <TableCell>Printed</TableCell>
              <TableCell>Error</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleEntries.map((e) => (
              <TableRow
                key={e.id}
                hover
                sx={isStuck(e) ? { bgcolor: 'warning.light' } : undefined}
              >
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      size="small"
                      label={e.status}
                      color={statusColor[e.status] ?? 'default'}
                    />
                    {isStuck(e) && (
                      <Tooltip title="Claimed but not finished in over 2 minutes">
                        <Chip size="small" label="stuck" color="warning" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {e.children
                    ?.map((c) => `${c.childFirstName} ${c.childLastName}`)
                    .join(', ')}
                </TableCell>
                <TableCell>{e.requestedBy ?? '—'}</TableCell>
                <TableCell>{e.date ?? '—'}</TableCell>
                <TableCell>{relative(e.requestedAt)}</TableCell>
                <TableCell>{relative(e.claimedAt)}</TableCell>
                <TableCell>{relative(e.printedAt)}</TableCell>
                <TableCell>
                  {e.error && (
                    <Typography variant="caption" color="error">
                      {e.error}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {e.status === 'error' && (
                    <Tooltip title="Retry (set back to pending)">
                      <IconButton size="small" onClick={() => retry(e.id)}>
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {isStuck(e) && (
                    <Tooltip title="Reset stuck job to pending">
                      <IconButton size="small" onClick={() => resetStuck(e.id)}>
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete job">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeJob(e.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {visibleEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No jobs to show.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <Button
          size="small"
          href="/checkin/load-test"
          variant="text"
        >
          Go to Load Test Page
        </Button>
      </Box>
    </Container>
  );
};

export default PrintQueueMonitor;
